import { onLifeAreaReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { Task } from 'shared/types/task';

import { useLifeAreaTree } from '../../hooks/use-life-area-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './life-area-grouping-tasks-list.style';
import {
  LifeAreaRenderData,
  LifeAreaRenderDataContext,
} from './life-area-render-context';
import { LifeAreaRenderItem } from './life-area-render-item';

const taskItemTypes = [TaskListItemTypes.Task];

const dropTypes = {
  [TaskListItemTypes.Root]: [],
  [TaskListItemTypes.LifeArea]: [
    TaskListItemTypes.Task,
    TaskListItemTypes.Goal,
  ],
  [TaskListItemTypes.Goal]: [TaskListItemTypes.Task],
  [TaskListItemTypes.GoalDeadline]: [],
  [TaskListItemTypes.Task]: [],
  [TaskListItemTypes.Priority]: [],
  [TaskListItemTypes.Date]: [],
  [TaskListItemTypes.DayPart]: [],
  [TaskListItemTypes.Tag]: [],
  [TaskListItemTypes.Type]: [],
  [TaskListItemTypes.NewTask]: [],
  [TaskListItemTypes.Section]: [],
};

export const LifeAreaGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  lifeAreas,
  goals,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const lifeAreasMap = useMemo(
    () =>
      lifeAreas.reduce<Record<ID, LifeArea>>((acc, lifeArea) => {
        acc[lifeArea.id] = lifeArea;
        return acc;
      }, {}),
    [lifeAreas],
  );
  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeItems = useLifeAreaTree({
    lifeAreas,
    goals,
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const lifeAreaIds = useMemo(() => Object.keys(lifeAreasMap), [lifeAreasMap]);

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onLifeAreaReorder({
      updatedItems,
      oldItems: Object.values(treeItems),
      tasksMap,
      lifeAreaIds,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  const renderData = useMemo<LifeAreaRenderData>(
    () => ({
      groupSettings,
      tasksMap,
      tasks,
      lifeAreasMap,
      lifeAreas,
      goals,
      today,
      denseFormat,
      defaultNewTaskDate,
    }),
    [
      groupSettings,
      tasksMap,
      tasks,
      lifeAreasMap,
      lifeAreas,
      goals,
      today,
      denseFormat,
      defaultNewTaskDate,
    ],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <LifeAreaRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeItems}
          renderItem={LifeAreaRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          dropTypes={dropTypes}
          isContextMenuOpen={isContextMenuOpen}
        />
      </LifeAreaRenderDataContext.Provider>
    </Styled.Container>
  );
};
